import { useEffect } from 'react';

/**
 * Constance PHYSIQUE de l'interface : la regle du fondateur, "10 cm sur le
 * portable = 10 cm sur le moniteur".
 *
 * Un navigateur ne connait pas les centimetres d'un ecran, mais il connait sa
 * resolution logique (window.screen) et sa densite (devicePixelRatio). On en
 * deduit une estimation de la taille physique d'un pixel CSS, en s'appuyant
 * sur les formats de dalle standard, et on corrige la racine rem pour que la
 * taille de reference (celle validee sur le MacBook 15 pouces, ~110 px CSS
 * par pouce) soit physiquement constante d'un ecran a l'autre.
 *
 * Cas couverts (les configurations reelles du marche) :
 * - Portables (largeur logique <= 1600, MacBook et Windows scale) : reference.
 * - Moniteur externe 4K en "comme 1920x1080" (defaut macOS, ~82 px/pouce) :
 *   les pixels CSS sont physiquement enormes, on reduit (~0.74).
 * - Moniteur externe en "comme 2560x1440" (~109 px/pouce) : deja au niveau de
 *   la reference, quasi aucun changement.
 * - 4K pilote en natif sans mise a l'echelle (~163 px/pouce) : on agrandit.
 * Un ecran inconnu retombe sur la reference (facteur 1) : jamais pire
 * qu'avant. Estimation sur dalle 27 pouces pour les moniteurs : les formats
 * proches (24, 32 pouces) restent dans une tolerance de quelques pourcents.
 *
 * Mecanisme : `font-size` en pourcentage sur la racine rem ; tout le systeme
 * etant exprime en rem, l'interface entiere suit, au pixel pres. Le zoom
 * navigateur (Cmd + / -) et les preferences d'accessibilite se cumulent
 * naturellement. Aucune propriete `zoom`, aucun transform, aucun reglage
 * utilisateur : le changement d'ecran est detecte (resize + sondage leger) et
 * la correction suit toute seule.
 */

const POLL_MS = 1500;

/** Densite logique de reference : le MacBook 15 pouces valide par le fondateur. */
const REFERENCE_CSS_PPI = 110;

/** Largeur physique supposee d'un moniteur externe (dalle 27 pouces, 16:9). */
const MONITOR_WIDTH_INCHES = 23.5;

/** Bornes de securite du facteur de correction. */
const MIN_SCALE = 0.7;
const MAX_SCALE = 1.5;

/** Signature de l'ecran qui porte la fenetre. */
function displayKey(): string {
  return `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio}`;
}

/**
 * Facteur de correction physique de l'ecran courant.
 * facteur = densite logique estimee / densite de reference.
 */
export function displayScaleFactor(): number {
  const width = window.screen.width;

  // Portable ou ecran deja mis a l'echelle par l'OS : c'est la reference.
  // (Couvre les MacBook 1440/1470/1512 et les portables Windows en 125/150 %.)
  if (width <= 1600) return 1;

  // Moniteur externe : densite logique estimee sur une dalle 27 pouces.
  const estimatedCssPpi = width / MONITOR_WIDTH_INCHES;
  const factor = estimatedCssPpi / REFERENCE_CSS_PPI;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.round(factor * 100) / 100));
}

function applyScale(scale: number): void {
  document.documentElement.style.fontSize = scale === 1 ? '' : `${scale * 100}%`;
}

/**
 * Applique la correction physique de l'ecran courant a la racine rem, et la
 * met a jour quand la fenetre change d'ecran.
 */
export function useDisplayScale(): void {
  useEffect(() => {
    let currentKey = displayKey();
    applyScale(displayScaleFactor());

    function onMaybeDisplayChange(): void {
      const key = displayKey();
      if (key === currentKey) return;
      currentKey = key;
      applyScale(displayScaleFactor());
    }

    const poll = window.setInterval(onMaybeDisplayChange, POLL_MS);
    window.addEventListener('resize', onMaybeDisplayChange);
    return () => {
      window.clearInterval(poll);
      window.removeEventListener('resize', onMaybeDisplayChange);
      applyScale(1);
    };
  }, []);
}
